// Phase 5 bridge: convert a real FFTBG unit → a battleSim "combatant spec".
//
// Reuses the same combat libs the cards/fight view use (unitCombatStats,
// weapon/unarmed damage, evasion, and the abilityFormula models via
// abilityVsTarget) so the sim's numbers stay consistent with the rest of the
// app. APPROXIMATE: maps the clear damage/heal/revive/status/buff abilities and
// skips ones it can't classify. Monsters carry empty ClassSkills, so their kit is
// pulled from data.monsterSkills (the monster→intrinsic-skills snapshot) and their
// innate movement/undead traits read off classBase.innates.

import { SKILLSET_ORDER, MONSTER_ORDER } from './skillsetOrder.js';
import { unitCombatStats } from './unitStats.js';
import { weaponDamage, unarmedDamage } from './damage.js';
import { hitChance, evadeSources } from './evasion.js';
import { abilityVsTarget } from './abilityFormula.js';
import { unitElementProfile, parseItemElements } from './elements.js';
import { reflectable, silenceable, sparesCaster, sparesAllies, sparesEnemies, standSearch, verticalOf, followsTarget, trialKeepsMovers, cantTargetSelf, aiShareRoll, aiCoinFlip, evadeKind, damageMods, THROW_ITEMS, ABILITY_FLAGS, LUNGING, ELEMENTAL_BY_SURFACE } from './abilityFlags.js';
import { moveBits } from './fftPath.js';

// Reaction names the engine models (map the one that differs from API spelling).
// Reactions the engine resolves (battleSim targetedReaction / dealAttack). Abandon & Parry are NOT here — their
// effect is the doubled / weapon evade, already baked into the precomputed `evade` via hitChance(unit.ReactionSkill).
const REACTIONS = new Set(['Counter', 'Hamedo', 'Blade Grasp', 'Mana Shield', 'Auto Potion', 'Damage Split',
  'Counter Tackle', 'Meatbone Slash', 'Arrow Guard', 'Dragon Spirit', 'Regenerator',
  'HP Restore', 'MP Restore', 'Speed Save', 'Brave Save', 'Faith Save', 'PA Save', 'MA Save', 'Earplug',
  'Sunken State', 'Caution', 'Critical Quick', 'Absorb Used MP', 'Distribute', 'Catch', 'Counter Flood', 'Counter Magic']);
const mapReaction = (r) => (r === 'Auto Potion' ? 'Auto-Potion' : (REACTIONS.has(r) ? r : null));

// A weapon's reach. FFTBG's item `info` carries the authoritative PER-ITEM range
// ("9 WP, 5 range, 15% evade, Bow…"), which varies a lot — crossbows 4, Windslash Bow
// 5, Long Bow 6, spears 2, books 3 — so read it from there. RANGED[type] is only a
// fallback when an item's info lacks a range; 1 = plain melee. (A past bug hardcoded
// every bow to 4: a range-5 Windslash Bow then couldn't hit a foe 5 tiles away and,
// boxed in by void from closing the last tile, the archer never fired. It also
// under-ranged spears to 1 and books to 2.)
const RANGED = { Bow: 4, Crossbow: 4, Gun: 5, Harp: 3, Book: 2 };
const weaponRange = (item) => {
  const m = item && item.info && item.info.match(/(\d+)\s*range/i);
  return m ? parseInt(m[1], 10) : (RANGED[item && item.type] || 1);
};

// Undead monster classes. FFTBG's undead are the two classic sprite families —
// the skeleton family (SUKERU) and the ghost family (YUREI). The mustad.io API
// does NOT expose Auto-Undead as an innate (verified: no class mentions Undead
// anywhere), so the engine can't read it off classBase.innates — this explicit
// list stands in. Healing damages these, and they ~50%-revive instead of
// crystallizing (battleSim). The Zombie/Undead STATUS is also honored at runtime.
const UNDEAD_MONSTERS = new Set(['Skeleton', 'Draugr', 'Reaper', 'Ghoul', 'Ghost', 'Revenant']);
// Innate movement traits that let a unit ignore terrain HEIGHT when pathing
// (birds/dragons/eyes fly; ghosts/bombs levitate) → a large effective Jump.
const HEIGHT_FREE_INNATES = new Set(['Fly', 'Ignore Height', 'Levitate']);
// Innates that also clear VOID/gap tiles, not just height: Fly (true flight) and
// Teleport (ghosts — move anywhere). These glide over chasms a ground unit (or a
// mere height-ignorer/levitator) is walled off by. → battleSim `canFly`.
const FLY_INNATES = new Set(['Fly', 'Teleport']);


// Math Skill (Calculator) parameters. FFTBG ships only two ATTRIBUTES — CT and the
// standing Height — and four NUMBERS (×3/×4/×5 and Prime); there is NO Level/Exp
// targeting. A Calculator that knows ≥1 attribute and ≥1 number can cast a charged
// spell it knows on EVERY unit whose attribute satisfies the number — instantly, for
// free, board-wide, friendly fire. (`CALC_ATTR` maps the param name → the combatant
// field the engine reads: `ct` and `z` are both already tracked.)
const CALC_ATTR = { CT: 'ct', Height: 'z' };
const CALC_NUM = { 'Prime Number': 'prime', '3': '3', '4': '4', '5': '5' };

const intIn = (info, re) => { const m = info.match(re); return m ? parseInt(m[1], 10) : 0; };
// The order the game's AI weighs the abilities in (skillsetOrder.js): the primary skillset's, then the secondary's, each
// in its skillset's own order, which is how ties between equal options fall. The site's lists aren't always in it:
// FFTBG's Calculator weighed Lucavi Skill as Quake, Tornado, Melt, where the site lists Melt, Tornado, Quake, and cast
// Quake where Tornado scored the same. A list goes by the named skillset, else by the one that holds most of it.
function gameOrder(names, setName = null) {
  let order = setName && SKILLSET_ORDER[setName];
  if (!order || !names.some((n) => order.includes(n))) {
    let best = 0; order = null;
    for (const o of Object.values(SKILLSET_ORDER)) { const k = names.filter((n) => o.includes(n)).length; if (k > best) { best = k; order = o; } }
  }
  if (!order) return names;
  const at = (n) => { const i = order.indexOf(n); return i < 0 ? Infinity : i; };
  return names.map((n, i) => [n, i]).sort((a, b) => (at(a[0]) - at(b[0])) || (a[1] - b[1])).map(([n]) => n);
}
// ...and a monster's kit in its own skillset's order (a '*' marks the Beastmaster-locked one; it keeps its mark)
function monsterOrder(cls, kit) {
  const order = MONSTER_ORDER[cls]; if (!order) return kit;
  const at = (raw) => { const i = order.indexOf(String(raw).replace(/\*$/, '')); return i < 0 ? Infinity : i; };
  return kit.map((n, i) => [n, i]).sort((a, b) => (at(a[0]) - at(b[0])) || (a[1] - b[1])).map(([n]) => n);
}
// abilityVsTarget returns a Random(lo,hi) damage as an array [lo,hi]; the engine wants
// a scalar power, and an array silently coerced to NaN (`[21,189] * mult` → NaN), so the
// AI's scorer scored those abilities NaN and never fired them — Pummel, Dash, Throw Stone,
// Cat Kick, Tail Swing were 5 permanently-dead skills. Collapse to the expected value.
const scalarPow = (p) => (Array.isArray(p) ? Math.round((p[0] + p[1]) / 2) : p);
const wItem = (it) => (it && it.type ? { type: it.type, wp: it.wp || 0, element: it.element || null, ...(it.heals ? { heals: true } : {}) } : null);
// The caster's side of a damage formula, as plain data (battle repros serialize actions).
const formulaCtx = (ctx) => {
  const o = {};
  for (const k of ['pa', 'ma', 'speed', 'brave', 'faith', 'wp', 'maxHp', 'weaponType', 'paUnarmed', 'martialArts', 'strengthen', 'attackUp', 'magicAttackUp']) if (ctx[k] != null && ctx[k] !== false) o[k] = ctx[k];
  return o;
};
// Enemy debuffs (status kind) and ally buffs (buff kind) an ability can inflict.
// Darkness → the engine's blind (halves physical accuracy); Frog/Berserk/Don't
// Move are modeled in battleSim. 'Death' = instant KO. Death Sentence is handled
// separately (a delayed-death countdown) before this matches.
const DISABLE = /Add\b[^.]*\b(Death|Stop|Sleep|Don't Act|Don't Move|Petrify|Charm|Frog|Berserk|Darkness|Silence|Confusion|Slow|Poison|Oil|Innocent|Undead)\b/;
const BUFF = /Add\b[^.]*\b(Haste|Protect|Shell|Regen|Reraise|Reflect|Float|Faith|Defending)\b/;
// Statuses a "Cancel …" clause can strip, split into the ones it's GOOD to remove from an
// ally (afflictions) vs. GOOD to strip from a foe (a Dispel of their buffs). Drives whether
// a cleanse targets allies (Esuna) or enemies (Dispel Magic).
const AFFLICTIONS = new Set(['Petrify', 'Darkness', 'Confusion', 'Silence', 'Berserk', 'Frog', 'Poison', 'Sleep', 'Stop', "Don't Move", "Don't Act", 'Slow', 'Death Sentence', 'Oil', 'Undead', 'Blood Suck', 'Charm', 'Innocent']);
const GOODSTATUS = new Set(['Haste', 'Protect', 'Shell', 'Regen', 'Reraise', 'Reflect', 'Float', 'Faith', 'Defending', 'Transparent', 'Performing']);

// Stat modification (songs/dances ±N to a team; Mediator talk + Battle-Skill breaks ±N to
// one target; self-pumps like Accumulate). Reads the signed deltas out of the Effect clause.
const STAT_FIELD = { PA: 'pa', MA: 'ma', Speed: 'speed', Brave: 'brave', Faith: 'faith' };
const parseStatMods = (info) => {
  const eff = (info.match(/Effect:(.*)$/) || [, ''])[1]; // only the Effect clause (avoid "(PA + 81)" in a Hit% clause)
  const mods = []; const re = /([+-]\d+)\s*(PA|MA|Speed|Brave|Faith)\b/g; let m;
  while ((m = re.exec(eff))) mods.push({ stat: STAT_FIELD[m[2]], delta: parseInt(m[1], 10) });
  return mods;
};
// Evaluate an additive "Hit: (PA + WP + 40)%" clause with the caster's stats → a % (for
// abilities abilityVsTarget doesn't score, e.g. the PA+WP Rends).
const hitPercent = (info, ctx) => {
  const m = info.match(/Hit:[^.(]*\(([^)]*)\)%/);
  if (!m) return 100;
  let sum = 0;
  for (const tok of m[1].split('+').map((s) => s.trim())) {
    if (/^\d+$/.test(tok)) sum += +tok;
    else sum += ({ PA: ctx.pa, WP: ctx.wp || 0, Speed: ctx.speed, MA: ctx.ma, Brave: ctx.brave, Faith: ctx.faith }[tok] || 0);
  }
  return Math.max(0, Math.min(100, sum));
};
// The parts of a "Hit: [Human;] [Faith](MA + 34)%" clause, for the per-target rate battleSim computes
// (successOf): the stat part s (the caster's stats summed), the constant k, and whether Faith gates it.
const hitParts = (info, ctx) => {
  const m = info.match(/Hit:\s*(?:[A-Za-z ]+;\s*)?(Faith)?\(([^)]*)\)%/);
  if (!m) return null;
  let st = 0, k = 0;
  for (const tok of m[2].split('+').map((x) => x.trim())) {
    if (/^\d+$/.test(tok)) k += +tok;
    else st += ({ PA: ctx.pa, WP: ctx.wp || 0, Speed: ctx.speed, MA: ctx.ma, Brave: ctx.brave, Faith: ctx.faith }[tok] || 0);
  }
  return { s: st, k, faith: !!m[1], ...(ctx.magicAttackUp ? { mau: true } : {}), ...(ctx.attackUp ? { au: true } : {}), ...(ctx.martialArts && !ctx.wp ? { ma: true } : {}) };
};
// How much a unit relies on magic, on MP and on evadable attacks, over the game's own list of its skills (its
// per-unit list at AI +3824, "Usable Ability Setting" 0x80199EC8): ⌊4 × count / n⌋, where n is the list's length
// and the counts skip the starred monster skill. The list is the basic Attack and a closing entry, every learned
// skill (a best-pick Throw once per item), the Jumps as one entry and Math Skill's CT / Height / Prime Number /
// number parts as one; a monster's is its four skills and the closing entry. Skills the sim can't play still
// count: FFTBG's Great Malboro listed Moldball Virus, its Grenade Self Destruct. A skill counts as magic when
// Silence blocks it (AI byte 1 & 0x01), as evadable on & 0x02 (and Charge+N, built from the weapon's record like
// the basic Attack), and for MP when it costs some. FFTBG's Calculator (Attack, Math Skill, eleven Yin-Yang spells,
// closing: 4 × 11 / 14 = 3, where we had 0) and Archer (four Charges: 4 × 5 / 9 = 2) now match. The AI prices
// Silence and Darkness by the magic and evadable ratings and a unit's MP by the MP one (AI +6200 / +6201 / +6199).
const MATH_PART = /^(CT|Height|Level|EXP|Prime Number|Prime|\d+)$/, JUMP = /^(Level|Vertical) Jump\d+$/;
function reliance(actions, monster, unit = null, data = {}) {
  const names = !unit ? [] : monster ? ((data.monsterSkills && data.monsterSkills[unit.Class]) || [])
    : [...(unit.ClassSkills || []), ...(unit.ExtraSkills || [])].filter((n) => n && n.trim());
  const byName = new Map(actions.map((a) => [a.name, a]));
  const evadable = (n, f) => (f && (f[9] & 0x02)) || (!monster && /^Charge\+\d+$/.test(n));
  let sil = 0, mp = 0, ev = monster ? 0 : 1, n = monster ? 1 : 2, jump = false, math = false;
  if (names.length) {
    for (const raw of names) {
      const locked = raw.endsWith('*'), name = raw.replace(/\*$/, '');
      if (name === 'Mimic') continue; // a Mime's list is Attack and the closing entry
      if (JUMP.test(name)) { if (!jump) { jump = true; n++; } continue; }
      if (!monster && MATH_PART.test(name)) { if (!math) { math = true; n++; } continue; }
      const a = byName.get(name), f = ABILITY_FLAGS[name];
      n += a && a.throwPick === 'best' && a.throwOpts ? a.throwOpts.length : 1;
      if (locked) continue;
      const cost = a ? (a.mp || 0) : +(((data.abilitiesInfo || {})[name] || '').match(/(\d+) MP/) || [])[1] || 0;
      if (f && (f[9] & 0x01)) sil++; if (evadable(name, f)) ev++; if (cost > 0) mp++;
    }
  } else { // no skill lists (a hand-built unit): our own actions
    const usable = actions.filter((a) => !a.locked && !a.mathCast);
    n += actions.some((a) => a.mathCast) ? 1 : 0;
    for (const a of usable) { const f = ABILITY_FLAGS[a.name]; if (f && (f[9] & 0x01)) sil++; if (evadable(a.name, f)) ev++; if ((a.mp || 0) > 0) mp++; n += a.throwPick === 'best' && a.throwOpts ? a.throwOpts.length : 1; }
  }
  const q = (c) => Math.floor(4 * c / n);
  return { magic: q(sil), mp: q(mp), phys: q(ev) };
}
// The Item skillset (Chemist), whose reach Throw Item raises to 4.
const ITEM_SKILLS = new Set(['Potion', 'Hi-Potion', 'X-Potion', 'Ether', 'Hi-Ether', 'Elixir', 'Antidote', 'Eye Drop', 'Echo Grass', "Maiden's Kiss", 'Soft', 'Holy Water', 'Remedy', 'Phoenix Down']);
const STRIP_SLOT = { 'head equipment': 'head', 'body equipment': 'armor', shield: 'shield', weapon: 'weapon', accessory: 'accessory' };

// Multi-status adds. "(Separate)" rolls EACH listed status independently, "(Random)" picks
// ONE, "(All)" applies them all on a single roll. Returns { statuses, mode } or null.
const STATUS_VOCAB = new Set(['Death', 'Stop', 'Sleep', "Don't Act", "Don't Move", 'Petrify', 'Charm', 'Frog',
  'Berserk', 'Darkness', 'Silence', 'Confusion', 'Slow', 'Poison', 'Oil', 'Innocent', 'Undead', 'Blood Suck',
  'Death Sentence', 'Haste', 'Protect', 'Shell', 'Regen', 'Reraise', 'Reflect', 'Float', 'Faith', 'Defending', 'Transparent']);
const multiStatus = (info) => {
  const m = info.match(/Add ([^.]*?)\s*\((Separate|Random|All)\)/);
  if (!m) return null;
  const mode = { Separate: 'each', Random: 'one', All: 'all' }[m[2]];
  const statuses = m[1].split(/,|\band\b/).map((s) => s.trim()).filter((s) => STATUS_VOCAB.has(s));
  return statuses.length >= 2 ? { statuses, mode } : null;
};

// One ability name + its API info string → a battleSim action spec (or null).
export function abilityToAction(name, info, ctx) {
  if (!info) return null;
  // The Holy and Mighty Sword skills (PA × (WP + N)) take the weapon's element: their routines (BATTLE.BIN formulas
  // 0x2D, 0x2E) check the elements with the weapon's byte, as the basic Attack does. FFTBG's Ninja with an Air Knife
  // (Wind) did half its Stasis Sword to a Wind-half foe and double to a Wind-weak one.
  // (marked weaponElement: Float's Earth immunity and Oil's doubling don't apply to a weapon's element, see battleSim)
  if (ctx && ctx.weaponElement && !/Element:/.test(info) && /PA \* \(WP \+ \d+\)/.test(info)) {
    const a = abilityToAction(name, info.replace(/Effect:/, `Element: ${ctx.weaponElement}. Effect:`), { ...ctx, weaponElement: null });
    return a && { ...a, weaponElement: true };
  }

  // JUMP (Lancer): a charged, unevadable physical leap. The parser-first path
  // rejects its "If armed, Damage (PA * WP)" clauses (verb-anchored regex), so
  // model it directly. Power mirrors jumpAttackModel/legacyAbilityFormula: armed
  // PA*WP (×3/2 with a Spear), barehanded [PA*Br/100]*PA. Charge = ⌊50/Speed⌋ (FFTBG's Knight, Speed 11,
  // landed its Jump on the 4th tick, the tick a foe's turn came up);
  // Jump ignores evasion (BMG) and is non-elemental. Level JumpN reaches N tiles
  // horizontally; Vertical JumpN reaches 1 tile but clears N of height.
  if (/\(jumping\)/.test(info)) {
    const wp = ctx.wp || 0;
    let power;
    if (wp) power = Math.floor(ctx.pa * wp * (ctx.weaponType === 'Spear' ? 1.5 : 1));
    else { const paU = ctx.paUnarmed || ctx.pa; power = Math.floor(paU * Math.floor((paU * ctx.brave) / 100)); }
    const vert = info.match(/(\d+) vertical range/);
    const range = vert ? (ctx.weaponRange || 1) : (intIn(info, /(\d+) range/) || ctx.weaponRange || 1);
    const charge = Math.max(1, Math.floor(50 / Math.max(1, ctx.speed)));
    return {
      name, kind: 'damage', power: Math.max(1, power), magical: false, target: 'enemy',
      range, aoe: 0, homing: false, linear: false, multihit: null, element: null,
      mp: 0, charge, ignoreEvade: true, noAttackUp: true, jump: true,
      ...(vert ? { vRange: parseInt(vert[1], 10) } : {}),
    };
  }

  // Revive items (Phoenix Down): a "Cancel Death" clause with NO Hit% gate is a
  // 100% item revive — on a fallen ally it raises them (at a low ~Random(1,20)
  // HP); on a LIVING undead it deals 100% MaxHP ("If Undead, Damage (100)%"), flagged
  // `undeadKill` + `undeadPct`. (Raise/Raise 2 carry a Faith Hit% and take the percent path below.)
  if (/Cancel Death/.test(info) && !/Hit:/.test(info)) {
    const aoeM = info.match(/(\d+)\s*AoE/);
    return {
      name, kind: 'revive', target: 'ally',
      range: intIn(info, /(\d+) range/) || ctx.weaponRange || 1,
      aoe: aoeM ? Math.min(parseInt(aoeM[1], 10), 4) : 0,
      homing: false, linear: false, multihit: null, element: null,
      mp: intIn(info, /(\d+) MP/), charge: intIn(info, /(\d+) CT/),
      power: 10, success: 100, undeadKill: true, undeadPct: intIn(info, /If Undead, Damage \((\d+)\)%/) || 100, // any Cancel Death sears a living undead unit
    };
  }

  // Martial Arts (Monk innate / support) → ×3/2 on PA-stat Punch Art abilities
  // (damage, Chakra heal, PA-based status success). ctx.martialArts is set in
  // prepCombatant; the lib only applies it to flagged ability models, so the
  // basic attack (already ×1.5 via unarmedDamage) isn't double-counted.
  // The attacker's own XA modifiers ride in ctx (prepCombatant sets them): Strengthen for the
  // elements its gear or class strengthens, Attack Up / Magic Attack Up. They scale the stat term
  // before the formula multiplies it out, each step floored, as the game does.
  const attMods = { martialArts: !!ctx.martialArts, ...(ctx.strengthen && ctx.strengthen.length ? { strengthen: new Set(ctx.strengthen) } : {}), ...(ctx.attackUp ? { attackUp: true } : {}), ...(ctx.magicAttackUp ? { magicAttackUp: true } : {}) };
  const avt = abilityVsTarget(info, ctx, attMods);
  // "N range" — an explicit "0 range" means the effect is CENTERED ON THE CASTER
  // (Draw Out, Monk punches, monster self-blasts), not aimed at a foe. Don't let the
  // weapon-range fallback clobber a real 0 (a past bug parsed "0 range" then, since 0
  // is falsy, overwrote it with the weapon range — so Asura became a ranged AoE the AI
  // aimed AT an enemy, and its 2-AoE blast spread across the field and hit the caster).
  const rangeM = info.match(/(\d+) range/);
  let range = rangeM ? parseInt(rangeM[1], 10) : (/move range/.test(info) ? (ctx.move || 4) : (ctx.weaponRange || 1));
  // AoE shape matters: the number is a DIAMOND radius for plain AoE, but
  // "(linear)" is a thin line (rarely catches the whole cluster here) and
  // "(homing)" keeps its radius (255 = team-wide songs/dances).
  const aoeM = info.match(/(\d+)\s*AoE(?:\s*\(([^)]+)\))?/);
  let aoe = 0, homing = false, linear = false;
  if (aoeM) {
    const n = parseInt(aoeM[1], 10);
    const shape = (aoeM[2] || '').toLowerCase();
    homing = /homing/.test(shape); // songs/dances/team abilities — seek a whole team, NO friendly fire
    linear = /linear/.test(shape); // a straight ray from the caster (Earth Slash etc.) — aoe carries its LENGTH
    // A line's length is its AoE number ("8 range, 8 AoE (linear)"); the breath/ink lines read
    // "N range, 0 AoE (linear)" and reach out to their range: FFTBG's Minotaur Blow Fires hit units
    // 1-3 tiles away in a straight line. Read as length 0 they hit nothing and were never used.
    aoe = linear ? (n || range) : (homing ? n : Math.min(n, 4));
  }
  // A 0-range ability is self-centered: the effect radiates from the caster's own tile
  // rather than an aimed point. (Songs/dances are 0-range too but already team-scoped
  // via `homing`; linear rays fire from the caster via `linear` — neither needs this.)
  const centerSelf = !!rangeM && range === 0 && !homing && !linear;
  // a song or dance ("(recurring)"): it passes over sleeping units (FFTBG's look-ahead, run in the interpreter: a
  // Wiznaibus that hit a Monk for 13 left him alone once he was asleep; a Galaxy Stop, homing but not a performance,
  // still caught sleepers)
  const performance = homing && /\(recurring\)/.test(info);
  // "(multihit)" — N strikes, each landing on a RANDOM panel in the AoE area
  // (Obsidian Blade & co.). N is fixed ("Hit: 3 times") or a range ("Random(1,10)").
  let multihit = null;
  if (aoeM && /multihit/.test((aoeM[2] || '').toLowerCase())) {
    const rnd = info.match(/Hit:\s*Random\((\d+),\s*(\d+)\)/i);
    const fixed = info.match(/Hit:\s*(\d+)\s*times/i);
    multihit = rnd ? { lo: +rnd[1], hi: +rnd[2] } : fixed ? { lo: +fixed[1], hi: +fixed[1] } : null;
  }
  const element = (info.match(/Element:\s*([A-Za-z]+)/) || [])[1] || (avt && avt.element) || null; // for defender absorb/half/weak/cancel (Throw carries it on the model)
  // Reflect bounces an ability by the game's own per-ability flag (abilityFlags.js), not by whether
  // its formula uses MA: monster skills such as Mutilate and Life Drain don't bounce, and status
  // spells such as Frog and Petrify do. Silence likewise blocks only what the game flags. Both are
  // left out when the ability isn't in the table.
  const refl = reflectable(name), sil = silenceable(name);
  // Who its area can catch, by the game's flags (+4: 0x01 never the caster, 0x40 never allies, 0x80
  // never enemies): a Stasis Sword never cuts its own Knight, Spirit of Life heals the Treant's allies
  // but not the Treant.
  const spare = { ...(sparesCaster(name) != null ? { sparesCaster: sparesCaster(name) } : {}), ...(sparesAllies(name) ? { sparesAllies: true } : {}), ...(sparesEnemies(name) ? { sparesEnemies: true } : {}) };
  // "Chance to add X[, Y] [(Random)]" on a damaging skill: a status rider (one of the list when Random).
  // "(Separate)" lists and "Chance to cancel" aren't modelled yet.
  const rm = info.match(/Chance to add ([^.;(]+?)\s*(\((Random)\))?\s*(?:[.;]|$)/);
  const rider = rm && !/\(Separate\)/.test(rm[0]) ? { statuses: rm[1].split(/,\s*/).map((x) => x.trim()).filter(Boolean), mode: rm[3] ? 'one' : 'all' } : null;
  // "DamageCaster (...)": HP the caster pays each use (Energize/Wish/Bequeath Bacon MaxHP/5, Destroy PA*5/2,
  // Compress PA*5, Dispose PA). Self Destruct's DamageCaster (CurrentHP) isn't modelled.
  const dc = info.match(/DamageCaster \((MaxHP|PA)(?:\s*\*\s*(\d+))?(?:\s*\/\s*(\d+))?\)/);
  const selfCost = dc ? Math.round((dc[1] === 'MaxHP' ? (ctx.maxHp || 0) : (ctx.pa || 0)) * (dc[2] ? +dc[2] : 1) / (dc[3] ? +dc[3] : 1)) : 0;
  // Where the AI stands for it: every tile for short evadable strikes, else the safest one (standSearch).
  const stand = standSearch(name, ctx.weaponRange || 1);
  // How far up or down it can be aimed and its area reaches, from the game's ability record (verticalOf).
  // A line reaches only units within its vertical of the height it's fired from (battleSim lineUnits; Earth Slash,
  // the breaths, Kikuichimonji are all 2), and so does a multihit's area: FFTBG's Hydra, Triple Flame (vertical 2)
  // centred at height 8, caught a Holy Dragon at 9 and not a Mediator at 12 or a Blue Dragon at 0 beside it.
  const vert = verticalOf(name);
  // How the AI's trial of a CHARGED cast counts who it hits (battleSim trialTargets): whether it follows
  // the unit it's aimed at, and whether units that act before it lands still count.
  const follows = followsTarget(name), keepsMovers = trialKeepsMovers(name), noSelf = cantTargetSelf(name), shareRoll = aiShareRoll(name), coinFlip = aiCoinFlip(name);
  // Which evasion can dodge it ('physical', 'magic' or 'none'), from the game's own formula code (evadeKind).
  const evade = evadeKind(name);
  // Which modifiers its damage takes (zodiac / defense / criticals / elements), from the same formula code.
  const modMask = damageMods(name);
  const hit = hitParts(info, ctx);
  // "Hit: Human;" (the talk skills) and "Hit: Undead;" (Seal Evil) say what the ability can affect at all.
  const only = (info.match(/Hit:\s*(Human|Undead|Different Sex);/) || [])[1];
  // a skill that takes the weapon's range (the game's flag) lunges with a spear, pole or cloth: straight lines only
  const lunge = !rangeM && LUNGING.has(ctx.weaponType) && !!(ABILITY_FLAGS[name] && (ABILITY_FLAGS[name][0] & 0x20));
  // ...and with a bow, crossbow or gun it shoots as the weapon does: clear of the melee height limit, and a gun
  // gains no range from height. FFTBG's Knight with a gun Armor Rended a foe 8 panels off and 7 below, from the
  // panel its Attack used; ours, holding the Rend to the 3-step melee limit, had to stand elsewhere.
  const weaponShot = !rangeM && /weapon range/.test(info) && (ctx.weaponRange || 1) >= 4;
  // Line of fire (battleSim lineOfFire): a "direct" skill (record byte +6 & 0x20: Throw Stone, the Souls, Holy Breath,
  // Blaster...) needs a clear shot, checked by the AI's stand search; a skill that takes the weapon's range (byte +3
  // & 0x20: the Aims, Rends and Breaks) needs one only with a direct weapon, and only when it's carried out.
  const lof = ABILITY_FLAGS[name] ? (ABILITY_FLAGS[name][3] & 0x20 ? 'aim' : ABILITY_FLAGS[name][0] & 0x20 ? 'weapon' : null) : null;
  // A direct line (Blow Fire, Black Ink, Holy Breath, Moldball Virus) is a direct shot along a row or column, not a
  // ray through everyone on it: the first unit in its path takes it, and terrain in the way stops it. FFTBG's own
  // trial (0x8019D308, run in the interpreter with the option moved to other panels) had a Minotaur's Blow Fire from
  // 2 panels behind its ally hit only that ally, never the foe 3 panels out, and give nothing at all aimed past an
  // empty panel or into a wall. The undirected lines (Earth Slash, the breaths) pass through.
  const straight = linear && lof === 'aim';
  if (straight) { linear = false; aoe = 0; }
  // The game's flag byte the reactions read (0x801938F6, the ability record's +6 = ABILITY_FLAGS [3]: 0x10 counterable,
  // 0x80 Counter Flood, 0x40 Counter Magic), read off the calc routine in the interpreter
  const reactFlags = ABILITY_FLAGS[name] ? ABILITY_FLAGS[name][3] : undefined;
  const base = { name, range, aoe, homing, linear, multihit, element, centerSelf, ...(reactFlags != null ? { reactFlags } : {}), ...(homing ? { performance } : {}), ...(lunge ? { lunge } : {}), ...(straight ? { straight } : {}), ...(weaponShot ? { projectile: true, ...(ctx.weaponType === 'Gun' ? { direct: true } : {}) } : {}), ...(lof ? { lof } : {}), mp: intIn(info, /(\d+) MP/), charge: intIn(info, /(\d+) CT/), ...(stand ? { standSearch: stand } : {}), ...(vert ? { vReach: vert.reach, vSpread: vert.spread } : {}), ...(refl != null ? { reflectable: refl } : {}), ...(sil != null ? { silenceable: sil } : {}), ...(rider ? { rider } : {}), ...(selfCost ? { selfCost } : {}), ...(follows != null ? { follows } : {}), ...(keepsMovers ? { keepsMovers } : {}), ...(noSelf ? { noSelf } : {}), ...(hit ? { hit } : {}), ...(only ? { only } : {}), ...(shareRoll ? { shareRoll } : {}), ...(coinFlip ? { coinFlip } : {}), ...(evade ? { evade } : {}), ...(modMask != null ? { modMask } : {}), ...spare };
  // "Heal (MaxHP * N / D)" heals a fraction of the CASTER's max HP (FFTBG's Energize healed 152 on units of any
  // size, its 380-HP Byblos paying 76): a fixed amount, not the target's share. It parsed as a 0 heal.
  const hm = info.match(/Effect:\s*Heal \(MaxHP \* (\d+) \/ (\d+)\)/);
  if (hm) return { ...base, kind: 'heal', power: Math.round((ctx.maxHp || 0) * +hm[1] / +hm[2]), target: 'ally', faith: false };

  // ===== Effects abilityVsTarget can't model (no stat/hit formula) — handled before the
  // avt gate. =====
  // Full-restore item (Elixir): Heal + HealMP, both as a % of MAX.
  if (/Heal \(\d+\)%/.test(info) && /HealMP \(\d+\)%/.test(info)) {
    return { ...base, kind: 'heal', pct: intIn(info, /Heal \((\d+)\)%/), mpPct: intIn(info, /HealMP \((\d+)\)%/), target: 'ally' };
  }
  // Self-referential HP damage (abilityVsTarget returns null for these). "Damage (MaxHP -
  // CurrentHP)" [× factor] = the CASTER's missing HP — "the more hurt I am, the harder I hit"
  // (Goblin Punch ×5/4, Vengeance, Lifebreak, Ulmaguest): ~0 at full HP, a finisher when low.
  // The Target-prefixed form is the TARGET's missing HP (Climhazzard). Computed from live HP at
  // resolution. (Self Destruct's suicide-AoE is excluded — it needs a self-KO + AI death cost.)
  if (!/DamageCaster/.test(info)) {
    const tm = /Damage \(TargetMaxHP - TargetCurrentHP\)/.test(info);
    const tmp = /Damage \(TargetCurrentMP\)/.test(info); // Manaburn: HP damage equal to the target's MP
    const t1 = /Damage \(TargetCurrentHP - 1\)/.test(info); // Demi 3: the target down to 1 HP, a magic, Faith-scaled hit like Demi's
    const cm = info.match(/Damage \(\(?MaxHP - CurrentHP\)?(?:\s*\*\s*(\d+)\s*\/\s*(\d+))?\)/);
    if (tm || tmp || t1 || cm) {
      return {
        ...base, kind: 'damage', magical: t1, target: 'enemy', noAttackUp: true,
        ...(tm ? { targetMissingHp: true } : tmp ? { targetCurrentMp: true } : t1 ? { targetToOne: true, faith: /Hit: Faith\(/.test(info) } : { casterMissingHp: cm[1] ? (+cm[1] / +cm[2]) : 1 }),
        ...(/Hit:/.test(info) ? { success: hitPercent(info, ctx) } : {}),
      };
    }
  }
  // Multi-status add (Bad Breath each-25% / Nameless Song one-of-5 @50% / Goo all-on-one-roll)
  // — handled here, before the avt gate, since the songs' flat "(50)%" Hit doesn't parse as a
  // formula (avt null). A self-cast (0-range/0-AoE, e.g. Fury) applies the WHOLE set to the
  // user — Fury's Berserk is an intentional downside (the +/- trade), NOT something to filter;
  // otherwise the side is decided by whether the set is all-beneficial.
  const ms = multiStatus(info);
  if (ms) {
    const selfCast = centerSelf && aoe === 0;
    const beneficial = !selfCast && ms.statuses.every((s) => GOODSTATUS.has(s));
    const success = /Hit:/.test(info) ? hitPercent(info, ctx) : (ms.mode === 'each' ? 25 : 100); // Separate w/ no Hit% (Bad Breath) = 25% each; Random/All no Hit% = 100%
    // (Fury's "If successful, Heal (35)%": the share of its own max HP it heals once the hit lands, statuses or not)
    const healPct = intIn(info, /If successful, Heal \((\d+)\)%/);
    return { ...base, kind: (selfCast || beneficial) ? 'buff' : 'status', status: ms.statuses[0], statuses: ms.statuses, statusMode: ms.mode,
      duration: 6, success, faith: !!(avt && avt.faith), target: selfCast ? 'self' : (beneficial ? 'ally' : 'enemy'), ...(healPct ? { healPct } : {}) };
  }
  // CT manipulation (Time Mage Quick → CT 100; bard Last Song → ally Quick; dancer Last Dance
  // → foe CT 0). Before the avt gate: the recurring songs' flat "(33)%" Hit gives no formula.
  const ctM = info.match(/Set CT to (\d+)/);
  if (ctM) { const ct = +ctM[1]; return { ...base, kind: 'ctset', ct, success: hitPercent(info, ctx), faith: !!(avt && avt.faith), target: ct >= 100 ? 'ally' : 'enemy' }; }
  // Status CLEANSE (Esuna/Stabilize Time strip afflictions off an ally; Dispel Magic strips
  // buffs off a foe; the Chemist cure-items Antidote/Soft/Eye Drop/Maiden's Kiss/Echo Grass/
  // Remedy/Holy Water + Odd Soundwave). Before the avt gate — the items carry no Hit% formula,
  // so they used to fall through to NULL. Side is decided by WHICH statuses it removes. ("Cancel
  // Death" is a raise, handled in the percent branch below, so it's excluded here.)
  // Steal Status ("Cancel statuses on target and add them to caster"): the game's record gives it status set 0x37, the
  // one Dispel Magic cancels, and its help names no statuses, so ours parsed two made-up ones and it was worth nothing.
  // FFTBG's Thief took a Geomancer's Reraise, Regen and Shell for itself.
  // Moldball Virus ("Transform target into a Malboro"): the target becomes that monster. simPrep's computeBaked hangs the
  // prepped monster on `into`.
  const tf = info.match(/Transform target into an? ([A-Za-z ]+?)\./);
  if (tf) return { ...base, kind: 'transform', into: tf[1].trim(), success: hitPercent(info, ctx), target: 'enemy' };
  if (/Cancel statuses on target and add them to caster/.test(info)) {
    return { ...base, kind: 'cleanse', statuses: ['Float', 'Reraise', 'Transparent', 'Regen', 'Protect', 'Shell', 'Haste', 'Faith', 'Reflect'], steal: true, success: hitPercent(info, ctx), faith: !!(avt && avt.faith), dispel: true, target: 'enemy' };
  }
  const canc = info.match(/Cancel\s+([^.;]+)/);
  if (canc && !/Cancel Death\b/.test(info)) {
    const statuses = canc[1].split(/,|\band\b/).map((s) => s.trim()).filter(Boolean);
    // Aimed at foes or at allies: the game's own AI flag says, where we have it (byte 0 of its AI table, 0x01 = it
    // helps its targets). Tickle has it clear, so FFTBG's Squire used it on a charging foe to undo the spell;
    // without the flags, mostly-buffs means a Dispel.
    const flags = ABILITY_FLAGS[name];
    const dispel = flags ? !(flags[8] & 0x01) : statuses.filter((s) => GOODSTATUS.has(s)).length > statuses.filter((s) => AFFLICTIONS.has(s)).length;
    return { ...base, kind: 'cleanse', statuses, success: hitPercent(info, ctx), faith: !!(avt && avt.faith), dispel, target: dispel ? 'enemy' : 'ally' };
  }
  // Guaranteed SINGLE status/buff with no Hit% gate (Guard "Add Defending"). A self-only
  // (0-range/0-AoE) buff targets the caster; a self-centered AoE buff its surrounding allies.
  if (!/Hit:/.test(info) && /Effect:[^.]*\bAdd\b/.test(info) && !/Damage|Heal|Absorb|Set CT|Cancel/.test(info)) {
    const buf = info.match(BUFF);
    if (buf) return { ...base, kind: 'buff', status: buf[1], duration: 6, success: 100, target: (centerSelf && aoe === 0) ? 'self' : 'ally' };
    const dis = info.match(DISABLE);
    if (dis) return { ...base, kind: 'status', status: dis[1], duration: 6, success: 100, target: 'enemy' };
  }

  // Steal / Rend — remove an equipment slot (modeled via the recompute foundation: blank
  // the slot + re-derive stats). Steal is Speed-gated, Rend is PA+WP-gated; both target a foe.
  const strip = info.match(/Remove target's (head equipment|body equipment|shield|weapon|accessory)/);
  // (The Mighty Sword skills take the gear and then strike: "If successful, Damage (PA * (WP + N))". The strike is `then`,
  // the damage half parsed on its own; it lands only when the strip does. FFTBG's Calculator chose Shellbust Stab and
  // Hellcry Crush, the biggest hits, where ours, counting the gear alone, took the cheapest, Icewolf Bite.)
  const then = strip && /If successful, Damage/.test(info) ? abilityToAction(name, info.replace(/Remove target's [^;]+;\s*If successful,\s*/, ''), ctx) : null;
  if (strip) return { ...base, kind: 'strip', slot: STRIP_SLOT[strip[1]], success: hitPercent(info, ctx), target: 'enemy', ...(/basic Attack instead/.test(info) ? { fallbackAttack: true } : {}), ...(then && then.kind === 'damage' ? { then } : {}) };

  // Stat modification (±N PA/MA/Speed/Brave/Faith). All-positive = a buff (own team / self);
  // any negative = a debuff (a foe). Songs/dances carry it team-wide via `homing`. PA/MA are
  // re-baked at resolution (the foundation); Speed/Brave/Faith are live.
  const statMods = parseStatMods(info);
  if (statMods.length) {
    const buff = statMods.every((m) => m.delta > 0);
    const success = hitPercent(info, ctx); // honor a flat "Hit: (50)%" (Slow Dance/Polka/Cheer Song — avt is null for the recurring songs, so it used to default to 100%)
    return { ...base, kind: 'statmod', mods: statMods, buff, success, faith: !!(avt && avt.faith),
      target: (centerSelf && aoe === 0) ? 'self' : (buff ? 'ally' : 'enemy') };
  }

  // (a % damage or drain with no formula of its own, Suck Blood's "AbsorbHP (25)%" with no Hit, goes on to the % branch)
  const pctEffect = /Effect:[^.]*?(Damage|AbsorbHP|AbsorbMP)\s*\((\d+)\)%/.test(info);
  if (!avt && !pctEffect) return null;

  // Throw (Ninja): a thrown weapon flies the unit's move range and is dodged by evade by facing (FFTBG's AI
  // listed a Shuriken at 76% from one panel and 100% from another; the old note said Throw can't be evaded).
  const isThrow = /Damage \(Speed \* ThrownWP\)/.test(info);
  // Summon Magic ignores Magic Evade entirely (BMG / FF Wiki) — an offensive summon never
  // "misses"; its only reliability lever is Faith (low/Innocent ⇒ ~0 damage, applied by
  // damageMult). In the data these are the positional `(homing)` damage spells (range > 0:
  // Shiva/Ramuh/Sylph/Bahamut/Flare 2…), as opposed to the 0-range homing dances. The
  // `Hit:`-gated summons (Moogle/Carbunkle/Lich) resolve via their success roll, not here.
  const summonUnevadable = base.homing && base.range > 0;
  // Faith-magic damage (`Damage Faith(MA*Q)`: Fire/Bolt/Meteor…) scales with the TARGET's
  // faith at resolution; flat MA damage (`Damage (MA*N)`: Draw Out, Choco Meteor, monster
  // breaths) does NOT — so flag it so damageMult only faith-scales the real faith spells.
  // `formula` keeps what the game's damage needs per target (battleSim formulaDamage): the info string
  // and the caster's numbers. `power` is the same formula against a Faith-50 neutral target, for the
  // places that only want a rough size. Attack Up is already in it, so it's marked noAttackUp.
  // What it can throw (abilityFlags THROW_ITEMS): Speed × each item's WP, with the item's element. Shuriken and
  // Bomb: the AI picks the best item for the target; a weapon throw is always one of its two items, by side (battleSim sideThrow).
  const ti = isThrow && THROW_ITEMS[name];
  if (ti) {
    // Each item carries its formula, Speed × its WP, so the damage is worked out per target the game's way: the
    // modifiers scale Speed, the XA term, with a floor at each step, and the WP multiplies after (FFTBG's Shuriken,
    // WP 4, always lands a multiple of 4: 14 Speed on a Bad match is (14 − 3) × 4 = 44; ×3/4 on the product gave 42).
    // No Attack Up or Martial Arts, as before (noAttackUp), and no Strengthen: the thrower's elemental gear doesn't lift
    // a thrown item (FFTBG's Water-strengthening Ninja's Torrent Bomb did 192 where ×5/4 gives 240).
    const fctx = { ...formulaCtx(ctx) }; delete fctx.attackUp; delete fctx.magicAttackUp; delete fctx.martialArts; delete fctx.strengthen;
    const opts = ti.items.map(([item, wp, el]) => ({ item, power: Math.max(1, (ctx.speed || 0) * wp), element: el, formula: { info: `${el ? `Element: ${el}. ` : ''}Effect: Damage (Speed * ${wp}).`, ctx: fctx } }));
    // a throw isn't held to a weapon's height limit: FFTBG's Dancer threw a Shuriken from 6.5 above its target
    // (Throw's skills, ids 0x17E on, aren't in the ability table that has the vertical flags)
    return { ...base, kind: 'damage', power: opts[0].power, element: opts[0].element, formula: opts[0].formula, magical: false, faith: false, target: 'enemy', noAttackUp: true, thrown: true, evade: 'physical', throwOpts: opts, throwPick: ti.pick, vReach: base.vReach ?? 99 };
  }
  if (avt && avt.kind === 'damage') return { ...base, kind: 'damage', power: scalarPow(avt.final), magical: avt.magical, faith: /Damage\s*Faith\(/.test(info), target: 'enemy', formula: { info, ctx: formulaCtx(ctx) }, noAttackUp: true, ...(summonUnevadable && !isThrow ? { ignoreEvade: true } : {}), ...(isThrow ? { thrown: true, evade: 'physical' } : {}) };
  // HP/MP drain with a stat formula (Night Sword AbsorbHP, Dark Sword AbsorbMP):
  // damage that feeds the caster. avt.magical marks the MA-stat (faith) form.
  if (avt && avt.kind === 'other' && (avt.verb === 'AbsorbHP' || avt.verb === 'AbsorbMP')) {
    const amt = parseInt(String(avt.display).replace(/[^\d]/g, ''), 10) || 0;
    // faith-based drains (Life Drain/Spell Absorb: AbsorbHP Faith(MA*Q)) scale with target
    // faith too; PA-based ones (Night/Dark Sword, Mutilate) stay flat.
    // The game runs a drain through the same damage routine as a hit (BATTLE.BIN formula 0x30, Night Sword: XA = PA,
    // YA = WP, then the physical modifiers, zodiac, Protect, Charging...), so the sim computes it per target as one:
    // FFTBG's armed Samurai with Martial Arts drained PA 11 × WP 13 = 143, where our baked number had the ×3/2 (208).
    return { ...base, kind: 'damage', power: amt, magical: !!avt.magical, faith: /Faith\(/.test(info), drain: avt.verb === 'AbsorbHP' ? 'hp' : 'mp', target: 'enemy', formula: { info: info.replace(/\bAbsorb(HP|MP)\b/, 'Damage'), ctx: formulaCtx(ctx) } };
  }
  // MP burn (Bizen Boat, Witch Hunt) and MP restore (Ether, Rehabilitate) with a stat
  // formula — drains/restores MP, never touching HP.
  if (avt && avt.kind === 'other' && avt.verb === 'DamageMP') {
    return { ...base, kind: 'mpDamage', power: parseInt(String(avt.display).replace(/[^\d]/g, ''), 10) || 0, magical: !!avt.magical, target: 'enemy' };
  }
  if (avt && avt.kind === 'other' && avt.verb === 'HealMP') {
    return { ...base, kind: 'mpHeal', power: parseInt(String(avt.display).replace(/[^\d]/g, ''), 10) || 0, target: 'ally' };
  }
  // %-of-MaxHP damage (Demi/Lich/Hurricane) or % drain (Drain Touch/Mutilate). The parser
  // skips the "(NN)%" damage clause, so read the percent + Hit: success from the raw info.
  const pm = info.match(/Effect:[^.]*?(Damage|AbsorbHP|AbsorbMP)\s*\((\d+)\)%/);
  if (pm) {
    const success = avt && avt.kind === 'percent' ? (parseInt(avt.display, 10) || 50) : 100;
    // AbsorbMP (NN)% (Siphon) drains a % of the target's MAX MP and feeds the caster.
    // Route it through mpDamage/pctMaxMp (like Mute's MP burn) with a drain flag — NOT
    // pctMaxHp, which would drain NN% of the target's MAX-HP-worth of MP (i.e. its
    // whole bar) and be mis-valued by the AI as a big HP nuke.
    if (pm[1] === 'AbsorbMP') return { ...base, kind: 'mpDamage', pctMaxMp: +pm[2], drain: 'mp', success, faith: !!(avt && avt.faith), target: 'enemy' };
    // "Add X; If successful, Damage (N)%" (Poison Nail): the status is the skill's main effect and comes with every hit
    // that lands, so it's a sure status, not a 19% rider (FFTBG's Red Panther's Poison Nail scored 46 = the 15% plus Poison)
    const sure = info.match(/Effect:\s*Add ([^;]+); If successful, (?:Damage|AbsorbHP)/);
    // ...and "AbsorbHP (N)%; Add X" (Suck Blood) the same, the other way round
    const sureAfter = !sure && info.match(/(?:Damage|AbsorbHP)\s*\(\d+\)%;\s*Add ([^.;]+)/);
    const onHit = sure || sureAfter ? (sure || sureAfter)[1].replace(/\s*\((?:All|Random|Separate)\)/, '').split(/,\s*/).map((x) => x.trim()).filter(Boolean) : null;
    // (Death: "If Undead, Heal (100)% instead")
    const undeadHeals = /If Undead, Heal/.test(info);
    // A "Chance to add" after an AbsorbHP (Blood Suck) is a rider in the battle (13% of 92 hits in FFTBG's logs), but
    // this routine's trial lands it: FFTBG's Calculator scored Blood Suck 148 on its target, the drain plus Blood Suck's
    // 116, and took it over a Blaster worth 116.
    const trialRider = pm[1] === 'AbsorbHP' && !!base.rider;
    return { ...base, kind: 'damage', pctMaxHp: +pm[2], magical: true, success, faith: !!(avt && avt.faith), drain: pm[1] === 'AbsorbHP' ? 'hp' : null, target: 'enemy', ...(onHit ? { onHit } : {}), ...(undeadHeals ? { undeadHeals } : {}), ...(trialRider ? { trialRider } : {}) };
  }
  if (avt.kind === 'other' && avt.verb === 'Heal') {
    const amt = parseInt(String(avt.display).replace(/[^\d]/g, ''), 10) || 0;
    // faith: a Cure-type heal (Faith(MA*Q)) scales with the TARGET's faith at resolution; one without
    // "Faith(" doesn't, MA-based or not (Spirit of Life, Heal (MA * 4), was scaled by mistake)
    return { ...base, kind: 'heal', power: amt, target: 'ally', faith: /Heal\s*Faith\(/.test(info) };
  }
  if (avt.kind === 'percent') {
    const success = parseInt(avt.display, 10) || 50;
    // Raise / Raise 2 / Revive: success is the Hit formula (Faith-scaled when it reads Faith(...)), the
    // raised unit comes back at "Heal (N)%" of ITS max HP, and on a living undead unit the revive kills
    // it, as in FFT (the game's Monk casts Raise 2 on an undead Draugr). `success` above is the heal %
    // here, not the hit chance, so it isn't used.
    if (/Cancel Death/.test(info)) { const pct = intIn(info, /Heal \((\d+)\)%/) || 50; return { ...base, kind: 'revive', pct, target: 'ally', success: hitPercent(info, ctx), faith: /Hit:\s*Faith\(/.test(info), undeadKill: true, undeadPct: pct }; } // (its heal, as damage on the undead: battleSim razeOf)
    // MP burn as a % of the target's MAX MP (Mute).
    const mpPm = info.match(/Effect:[^.]*?DamageMP\s*\((\d+)\)%/);
    if (mpPm) return { ...base, kind: 'mpDamage', pctMaxMp: +mpPm[1], success, faith: !!avt.faith, target: 'enemy' };
    // (CT manipulation "Set CT to N" and status CLEANSE "Cancel X" are handled before the avt
    // gate above — Quick/Last Song/Dance and Esuna/Antidote/Soft/Remedy/etc.)
    // Death Sentence is a delayed-death countdown (3 turns), NOT the instant-KO "Death".
    if (/Add\b[^.]*Death Sentence/.test(info)) return { ...base, kind: 'status', status: 'Death Sentence', duration: 3, success, target: 'enemy', faith: !!avt.faith };
    // A 0-range, 0-AoE status ability can only reach the CASTER — it's a self-buff, not
    // an enemy debuff. Fury ("Add Berserk, Haste (All)") trips the DISABLE regex on
    // "Berserk" and was mis-cast on a FOE; it's self-cast only, so apply the beneficial
    // status (Haste) to the caster. (Self-centered AoEs — aoe > 0, e.g. Bad Breath —
    // still ring-debuff foes via the normal path below.)
    if (centerSelf && aoe === 0) {
      const self = info.match(BUFF);
      if (self) return { ...base, kind: 'buff', status: self[1], duration: 6, success: 100, target: 'self' };
    }
    const dis = info.match(DISABLE);
    if (dis) return { ...base, kind: 'status', status: dis[1], duration: 6, success, target: 'enemy', faith: !!avt.faith };
    // A buff spell (Haste, Protect, Shell, Regen, Reraise, Float, Reflect, Pray Faith) is aimed at any ally in
    // range, its caster included. (These used to be marked self-only, so our White and Time Mages only ever
    // buffed themselves, and their allies only when standing in the area: FFTBG's Time Mage listed Haste on
    // the ally beside her at 74%, ours only on herself.)
    const buf = info.match(BUFF);
    if (buf) return { ...base, kind: 'buff', status: buf[1], duration: 6, success: 100, target: 'ally' };
  }
  return null;
}

// Compute every gear/stat-derived ("baked") field of a combatant from the unit + a
// mutable `state` of runtime changes: { paBonus, maBonus, removed:Set }. With an EMPTY
// state this reproduces the original prep exactly — so the no-change path (every normal
// battle) is byte-identical and needs no recalibration. PA/MA have no live runtime
// representation in the sim, so a stat-mod touching them (Phase B) and a steal/rend that
// drops gear (Phase C) re-run this to refresh power / evade / range / elements / HP.
// (Brave/Faith/Speed ARE live fields, adjusted directly, so they aren't re-baked here.)
function computeBaked(unit, data, state = {}) {
  // Brave and Faith as they stand now (Threaten, Praise, a Faith song...): the bare-hand punch, a monster's
  // PA*[PA*Br/100] and every Faith-scaled number are baked from them. FFTBG's Mime, Threatened from Brave 58 to 38,
  // punched for 13 * floor(13 * 38 / 100) = 52 where ours still had 91.
  if (state.brave != null || state.faith != null) unit = { ...unit, Brave: state.brave ?? unit.Brave, Faith: state.faith ?? unit.Faith };
  const { charStats, itemschanged, notAbles, abilitiesInfo } = data;
  const paBonus = state.paBonus || 0, maBonus = state.maBonus || 0;
  const rm = state.removed;
  // A stolen/rent slot is modeled by blanking that equip field and re-deriving stats
  // from scratch (the '' item is zero-stat, so the slot's bonuses vanish, e.g. armor HP).
  const eq = (rm && rm.size) ? {
    ...unit,
    Mainhand: rm.has('weapon') ? '' : unit.Mainhand,
    Offhand: rm.has('shield') ? '' : unit.Offhand,
    Head: rm.has('head') ? '' : unit.Head,
    Armor: rm.has('armor') ? '' : unit.Armor,
    Accessory: rm.has('accessory') ? '' : unit.Accessory,
  } : unit;
  const { classBase, equipped, stats, paUnarmed } = unitCombatStats(eq, charStats, itemschanged, notAbles);
  const isMA = unit.SupportSkill === 'Martial Arts' || unit.Class === 'Monk';
  const pa = stats.pa + paBonus, ma = stats.ma + maBonus, paU = paUnarmed + paBonus; // unit-level PA/MA mods

  // Mainhand basic-attack power for a given Charge +stat bonus (cBonus). weaponDamage
  // returns a STRING for elemental guns ("60% (X) - …") and random-range weapons
  // ("lo - hi"); coerce those to a usable number so the sim never gets NaN damage.
  // cBonus drives Charge+N (it shifts the primary stat and the gun's effective WP).
  const mainBasic = (cBonus = 0) => {
    let b = eq.Mainhand
      ? weaponDamage({ item: equipped.Mainhand, pa, ma, speed: stats.speed, brave: unit.Brave, faith: unit.Faith, doublehand: unit.SupportSkill === 'Doublehand', targetFaith: 50, paBonus: cBonus })
      : null;
    if (typeof b === 'string') {
      if (equipped.Mainhand.type === 'Gun') b = Math.floor((unit.Faith * 50 * ((equipped.Mainhand.wp || 0) + cBonus) * 16) / 10000);
      else { const ns = b.match(/\d+/g); b = ns ? ns.map(Number).reduce((a, x) => a + x, 0) / ns.length : null; }
    }
    if (b == null || !Number.isFinite(b)) b = unarmedDamage({ paUnarmed: paU, brave: unit.Brave, isMartialArts: isMA, paBonus: cBonus });
    return b;
  };
  let basic = mainBasic(0);

  // Dual Wield (the support OR the Ninja innate) → a second basic strike. With a real
  // off-hand weapon it's that weapon's own power + element; FULLY UNARMED it's a second
  // fist ("attacking once with each (or both fists if unarmed)" — the skill's own rule).
  // Shields (wp 0) and a lone weapon + empty hand stay single.
  const dualWield = unit.SupportSkill === 'Dual Wield'
    || ((classBase && classBase.innates) || []).some((i) => i && i.name === 'Dual Wield');
  let basic2 = 0, element2 = null;
  if (dualWield && equipped.Offhand && equipped.Offhand.wp > 0) {
    let b2 = weaponDamage({ item: equipped.Offhand, pa, ma, speed: stats.speed, brave: unit.Brave, faith: unit.Faith, doublehand: false, targetFaith: 50 });
    if (typeof b2 === 'string') {
      if (equipped.Offhand.type === 'Gun') b2 = Math.floor((unit.Faith * 50 * (equipped.Offhand.wp || 0) * 16) / 10000);
      else { const ns = b2.match(/\d+/g); b2 = ns ? ns.map(Number).reduce((a, b) => a + b, 0) / ns.length : 0; }
    }
    basic2 = Number.isFinite(b2) ? Math.max(1, Math.round(b2)) : 0;
    element2 = equipped.Offhand.element || null;
  } else if (dualWield && !eq.Mainhand) {
    basic2 = Number.isFinite(basic) ? Math.max(1, Math.round(basic)) : 0;  // both fists
  }

  const tgt = { equipped, cEv: (classBase && classBase.cEvPercent) || 0, reaction: unit.ReactionSkill };
  const ev = (o) => Math.round(100 - hitChance(tgt, o));
  const evade = {
    front: ev({ type: 'physical', direction: 'front' }), side: ev({ type: 'physical', direction: 'side' }),
    back: ev({ type: 'physical', direction: 'back' }), magic: ev({ type: 'magical' }),
  };
  // the same, source by source (battleSim's evadeHit applies Defending, Darkness and the like per attack)
  const evs = (o) => evadeSources(tgt, o);
  const evadeSrc = { front: evs({ type: 'physical', direction: 'front' }), side: evs({ type: 'physical', direction: 'side' }), back: evs({ type: 'physical', direction: 'back' }), magic: evs({ type: 'magical' }) };

  const wpRange = weaponRange(equipped.Mainhand);
  // Elements the unit's gear (or a monster's class) strengthens: ×5/4 on the stat term of its spells.
  const strengthen = [...unitElementProfile(equipped).strengthen, ...((classBase && classBase.effect && parseItemElements('Effect: ' + classBase.effect + '.').strengthen) || [])];
  const ctx = { pa, ma, speed: stats.speed, brave: unit.Brave, faith: unit.Faith, wp: equipped.Mainhand.wp, maxHp: stats.hp, targetFaith: 50, weaponRange: wpRange, move: stats.move, weaponType: equipped.Mainhand.type, weaponElement: equipped.Mainhand.element || null, paUnarmed: paU, martialArts: isMA,
    strengthen, attackUp: unit.SupportSkill === 'Attack Up', magicAttackUp: unit.SupportSkill === 'Magic Attack Up' };

  // Weapon element + magical-gun flag, shared by the basic attack and Charge+N.
  const wpElement = equipped.Mainhand && equipped.Mainhand.element ? equipped.Mainhand.element : null;
  const magicalGun = equipped.Mainhand.type === 'Gun' && !!equipped.Mainhand.element;

  const actions = [];
  if (unit.Gender !== 'Monster') {
    for (const n of [...gameOrder(unit.ClassSkills || []), ...gameOrder(unit.ExtraSkills || [], unit.ActionSkill)]) {
      // CHARGE+N (Archer): a basic attack aimed for N CT, with +N on the primary
      // stat (the "Basic Attack with +N" info clause has no Damage formula, so the
      // parser can't model it). Recompute the mainhand basic at paBonus N, charge
      // for the info's "N CT". It hits like a basic (weapon element, evadable).
      const chg = n.match(/^Charge\+(\d+)$/);
      if (chg) {
        const bonus = +chg[1];
        const info = abilitiesInfo[n] || '';
        actions.push({
          name: n, kind: 'damage', target: 'enemy', range: wpRange, aoe: 0, mp: 0, chargeN: bonus,
          charge: Math.max(1, intIn(info, /(\d+) CT/) || bonus + 2),
          power: Math.max(1, Math.round(mainBasic(bonus))), magical: magicalGun,
          element: wpElement, projectile: wpRange >= 4, homing: false, linear: false, multihit: null, ...(equipped.Mainhand.type === 'Gun' ? { direct: true } : {}),
          ...(LUNGING.has(equipped.Mainhand.type) ? { lunge: true } : {}), // Charge shoots with the weapon's range, so a spear (pole, cloth) lunges
        });
        continue;
      }
      const a = abilityToAction(n, abilitiesInfo[n], ctx);
      if (a) actions.push(a);
    }
  } else {
    // Monsters carry EMPTY ClassSkills/ExtraSkills in the tournament data — their
    // kit is class-intrinsic, looked up from the monster→skills snapshot
    // (data.monsterSkills, = the live toolTips.MonsterSkills). A trailing '*' marks
    // the Beastmaster-locked rare skill (the universal Wild Beast innate), usually
    // unavailable, so we drop it. The basic Attack is kept as a fallback (Wild Beast
    // technically suppresses it, but retaining it covers the monster skills the
    // formula parser can't yet model — see monsterSkillCoverage). Unparsed skills
    // are skipped.
    // The '*' skill is usable only from a panel next to an allied Beastmaster ("Allied monsters can use their
    // locked ability while adjacent to you"): FFTBG's Black Chocobo listed Choco Meteor only from panels
    // beside its Beastmaster Squire, five times of five. Kept, flagged `locked` (battleSim checks the panel).
    for (const raw of monsterOrder(unit.Class, (data.monsterSkills && data.monsterSkills[unit.Class]) || [])) {
      if (typeof raw !== 'string') continue;
      const name = raw.replace(/\*$/, ''), a = abilityToAction(name, abilitiesInfo[name], ctx);
      if (a) actions.push(raw.endsWith('*') ? { ...a, locked: true } : a);
    }
  }

  // Jump combines the best of both: Level JumpN gives the horizontal reach, Vertical JumpN the height, and a unit
  // that knows both jumps that far and that high either way (FFTBG's Geomancer with Level Jump4 and Vertical Jump7
  // jumped from a panel 3.5 above its target).
  { const lv = Math.max(0, ...actions.filter((a) => a.jump && /^Level Jump\d+$/.test(a.name)).map((a) => a.range || 0));
    const vt = Math.max(0, ...actions.filter((a) => a.jump && a.vRange != null).map((a) => a.vRange));
    for (let k = 0; k < actions.length; k++) { const a = actions[k]; if (!a.jump) continue;
      if (vt && /^Level Jump/.test(a.name)) actions[k] = { ...a, vRange: Math.max(a.vRange ?? 0, vt) };
      if (lv && /^Vertical Jump/.test(a.name)) actions[k] = { ...a, range: Math.max(a.range || 1, lv) }; }
    // ...and it's one command, so one option: FFTBG's Lancers listed "Level Jump" once, never a Vertical Jump beside it,
    // where ours had both (the same jump twice in the hit cascade). The Level Jump stands for it, the Vertical when it's
    // the only one.
    const jumps = actions.filter((a) => a.jump);
    if (jumps.length > 1) { const keep = jumps.reduce((b, a) => ((/^Level Jump/.test(a.name) ? 1 : 0) - (/^Level Jump/.test(b.name) ? 1 : 0) || (a.range || 0) - (b.range || 0)) > 0 ? a : b); for (const a of jumps) if (a !== keep) actions.splice(actions.indexOf(a), 1); } }

  // Throw Item (the Chemist's innate, or the support): the Item skillset reaches 4 panels, not the 1 its help
  // text gives (FFTBG's Chemist listed Phoenix Down and Soft on allies 4 panels from where it would stand).
  if (unit.SupportSkill === 'Throw Item' || ((classBase && classBase.innates) || []).some((i) => i && i.name === 'Throw Item')) {
    // (thrown that far, clear of the height limit, as Throw is: the same Squire threw a Potion 4 steps down)
    for (let k = 0; k < actions.length; k++) if (ITEM_SKILLS.has(actions[k].name)) actions[k] = { ...actions[k], range: 4, vReach: 99 };
  }
  // ...and a thrown item needs a clear line to its target: FFTBG's stand search ray-cast every panel a Squire's
  // Potion was weighed from (0x801B06D0), skipped the five whose line a ledge blocked and threw from the sixth.
  for (let k = 0; k < actions.length; k++) if (ITEM_SKILLS.has(actions[k].name)) actions[k] = { ...actions[k], lof: 'item' };
  {
  }

  // Attack Up / Magic Attack Up: +1/3 outgoing physical / magical damage (baked in).
  const atkUp = unit.SupportSkill === 'Attack Up', mAtkUp = unit.SupportSkill === 'Magic Attack Up';
  if (atkUp) { basic = Number.isFinite(basic) ? basic * 4 / 3 : basic; basic2 = Math.round(basic2 * 4 / 3); }
  if (atkUp || mAtkUp) for (const a of actions) {
    if (a.kind === 'damage' && !a.noAttackUp) { if (a.magical && mAtkUp) a.power = Math.round(a.power * 4 / 3); else if (!a.magical && atkUp) a.power = Math.round(a.power * 4 / 3); }
  }

  // Math Skill (Calculator): turn the unit's charged spells into INSTANT, FREE,
  // board-wide math-casts — one action per (spell × known attribute × known number)
  // that hits every unit whose CT/Height satisfies the number, friendly fire and all.
  // Only CHARGED magic is math-castable (FFT requires a CTR), and we apply it
  // single-target per matching unit (no AoE splash — the simple, dominant form). The
  // normal casts are kept too, so the AI falls back when no math lines up.
  // Not only Calculators: Math Skill as a secondary casts the unit's own spells the same way. FFTBG's Wizard with Math
  // Skill (CT, Height, Prime Number, 4) opened with an instant Death that killed a Samurai and a Sekhret (its Mime copied
  // it), in 15 of 20 battles; ours built it no math casts at all, and the sim gave its team 6% where the game gave 87%.
  if (unit.Class === 'Calculator' || unit.ActionSkill === 'Math Skill') {
    const known = new Set([...(unit.ClassSkills || []), ...(unit.ExtraSkills || [])]);
    const attrs = Object.keys(CALC_ATTR).filter((n) => known.has(n)).map((n) => CALC_ATTR[n]);
    const nums = Object.keys(CALC_NUM).filter((n) => known.has(n)).map((n) => CALC_NUM[n]);
    if (attrs.length && nums.length) {
      // Which spells Math Skill can cast is the game's own flag (ability flags +5 & 0x40): FFTBG's Math Skill Targeting
      // (0x8019BBBC) skips the rest. Slow, Fire 3, Blind, Reflect and Haste have it; Slow 2, Fire 4, Haste 2, Life
      // Drain and Cure 4 don't. (We used to take every charged damage, status or heal spell, so our Calculators
      // math-cast Slow 2 and Fire 4, and never Reflect.) Without the flags, the old rule.
      const mathFlag = (a) => { const f = ABILITY_FLAGS[a.name]; return f ? !!(f[2] & 0x40) : null; };
      const mathable = actions.filter((a) => !a.linear && !a.centerSelf && !a.multihit && !a.homing && !a.mathCast && a.kind !== 'mpHeal'
        && (mathFlag(a) ?? ((a.kind === 'damage' || a.kind === 'status' || a.kind === 'heal') && (a.charge || 0) > 0)));
      for (const spell of mathable) for (const attr of attrs) for (const key of nums) {
        actions.push({
          ...spell, mathCast: { attr, key }, baseName: spell.name, mp: 0, charge: 0, range: 99, aoe: 0,
          linear: false, multihit: null, homing: false, centerSelf: false,
          name: `${spell.name} [${attr === 'z' ? 'Height' : 'CT'}${key === 'prime' ? ':P' : '×' + key}]`,
        });
      }
    }
  }

  // Innate monster traits (humans have no innates in this data). Fly / Ignore
  // Height / Levitate ignore terrain height when pathing → effectively unlimited
  // Jump; Levitate also grants permanent Float (Earth-immune, lava-safe). Undead
  // (skeleton/ghost families) is a combat trait read by battleSim.
  const innates = (classBase && classBase.innates) || [];
  // (Beastmaster is also innate to Mimes, Goblins, Black Goblins and the Holy Dragon: FFTBG's Black Chocobo used Choco
  // Meteor from beside a Mime whose support was Sicken)
  const heightFree = innates.some((i) => i && HEIGHT_FREE_INNATES.has(i.name));
  const canFly = innates.some((i) => i && FLY_INNATES.has(i.name)); // glides over void gaps
  // permanent Float, from Levitate as a monster's innate or as a human's Move skill: FFTBG's Dancer with Levitate started
  // every battle floating (unit byte 0x5A & 0x40), so a spear 3.5 below her couldn't reach her; ours had her on the ground
  const floats = unit.MoveSkill === 'Levitate' || innates.some((i) => i && i.name === 'Levitate');
  const innateNames = new Set(innates.filter(Boolean).map((i) => i.name));
  const hasSupport = (name) => unit.SupportSkill === name || innateNames.has(name);
  const innateReaction = (innates.find((i) => i && i.type === 'react') || {}).name || '';
  // "Wild Beast: … prevents it from using the basic Attack action" (FFTBG's ability help).
  const wildBeast = innateNames.has('Wild Beast');
  // A monster's innate elements ride on its class entry ("Absorb Earth; Null Water; Weak Fire",
  // from classhelp.txt via fetchGameData). "Null" is the item wording's "Cancel".
  const elementProfile = unitElementProfile(equipped);
  if (classBase && classBase.effect) {
    const innate = parseItemElements('Effect: ' + classBase.effect.replace(/\bNull\b/g, 'Cancel') + '.');
    for (const k of Object.keys(elementProfile)) for (const el of innate[k] || []) elementProfile[k].add(el);
  }
  const undead = UNDEAD_MONSTERS.has(unit.Class);
  // Statuses from gear and class: "Permanent X" (always on), "Initial X" (starts the battle with it),
  // "Immune X" (can't be given it). Items list the first two as permStatuses/initialStatuses; all
  // three are in the help text, which is also where classes keep theirs ("Permanent Undead",
  // "Immune Float, Poison"). Ribbon-type immunities, Angel Ring's Initial Reraise, Reflect Mail's
  // Permanent Reflect: none of it reached the sim before.
  const traits = { perm: new Set(), init: new Set(), immune: new Set() };
  const clauses = (text) => {
    for (const m of String(text || '').matchAll(/\b(Permanent|Initial|Immune) ([^;.]+)/g)) {
      const set = m[1] === 'Permanent' ? traits.perm : m[1] === 'Initial' ? traits.init : traits.immune;
      for (const st of m[2].split(',').map((x) => x.trim()).filter(Boolean)) set.add(st);
    }
  };
  for (const it of Object.values(equipped || {})) if (it && it.info) clauses(String(it.info).split('Effect:')[1]);
  if (classBase) clauses(classBase.effect);
  if (floats) traits.perm.add('Float'); // Levitate
  // Immune to Death is immune to Death Sentence too: every unit FFTBG made death-immune (a Defense Ring, Ribbon,
  // Cachusha, Judo Outfit, a Steel Giant; 16 in 22 captures) carried the Death Sentence bit in its immunities (unit
  // +0x57 & 1) as well, though only the Panther Bag and Barette name it. Ours had its Mediator aim Death Sentence at a
  // Defense Ring Time Mage the game never listed it at.
  if (traits.immune.has('Death')) traits.immune.add('Death Sentence');

  const spec = {
    name: unit.Name, cls: unit.Class, gender: unit.Gender, sign: unit.Sign, raidBoss: !!unit.RaidBoss,
    hp: stats.hp, mp: stats.mp, speed: stats.speed, move: stats.move,
    jump: (heightFree || canFly) ? 99 : stats.jump, // fliers/levitators clear any height
    jumpStat: stats.jump,                                  // the real Jump, for FFT's pathfinder on a real map
    moveBits: moveBits([unit.MoveSkill, ...innateNames]), // its movement abilities, as the game's unit bits
    range: wpRange, power: Math.max(1, Math.round(Number.isFinite(basic) ? basic : 10)),
    // The basic Attack as plain data for the exact per-target damage (battleSim formulaDamage builds
    // damage.js's weapon model from it): main hand, the Dual Wield off hand (null = a second fist), and
    // the attacker's numbers. `power`/`power2` above stay as rough sizes.
    weapon: { main: wItem(eq.Mainhand ? equipped.Mainhand : null), off: wItem(dualWield && equipped.Offhand && equipped.Offhand.wp > 0 ? equipped.Offhand : null),
      support: unit.SupportSkill || '', cls: unit.Class,
      ctx: { pa, ma, speed: stats.speed, brave: unit.Brave, faith: unit.Faith, paUnarmed: paU, ...(unit.SupportSkill === 'Attack Up' ? { attackUp: true } : {}), ...(strengthen.length ? { strengthen } : {}) } },
    ...(canFly ? { canFly: true } : {}), // Fly/Teleport: glide over void/gap tiles too
    projectile: wpRange >= 4, // bows/guns/crossbows arc over height & gain range from high ground
    ...(equipped.Mainhand.type === 'Gun' ? { direct: true } : {}), // a gun fires direct: no extra range from high ground

    // Supports and reactions count whether equipped or innate to the class: nearly every monster
    // has an innate Counter (it countered 11 times in 14 battles in FFTBG's build), and some have
    // Defense Up (Dryad/Treant/Taiju), Magic Defense Up (Floating Eye/Ahriman/Plague) or Short
    // Charge (Archaic/Ultima Demon, Apanda). Their reaction slot is empty in the tournament data.
    brave: unit.Brave, faith: unit.Faith, reaction: mapReaction(unit.ReactionSkill || innateReaction),
    concentrate: hasSupport('Concentrate'),      // ignores target evade
    defenseUp: hasSupport('Defense Up'),          // -1/3 incoming physical
    magicDefenseUp: hasSupport('Magic Defense Up'), // -1/3 incoming magical
    shortCharge: hasSupport('Short Charge'),      // halves charge time (CTR)
    ...(hasSupport('Sicken') ? { sicken: true, gunBasic: equipped.Mainhand.type === 'Gun' } : {}), // physical attacks add Poison (not a gun's basic Attack)
    element: wpElement, // weapon element for the basic attack
    power2: basic2, element2, // Dual Wield: off-hand second strike (0 = none)
    elements: elementProfile, // defender absorb/half/weak/cancel: gear, plus a monster's innate ones
    ...(wildBeast ? { noBasic: true } : {}), // Wild Beast: no basic Attack action (Counter/Berserk still swing)
    // Blood Suck takes the unit over: it uses Suck Blood (AbsorbHP 25%, adds Blood Suck), which no unit has of its own
    ...(data.abilitiesInfo && data.abilitiesInfo['Suck Blood'] ? { suckBlood: abilityToAction('Suck Blood', data.abilitiesInfo['Suck Blood'], ctx) } : {}),
    // Counter Tackle retaliates with Dash ("PA * Random(1,4)"), whatever the unit's own skills
    ...(unit.ReactionSkill === 'Counter Tackle' && data.abilitiesInfo && data.abilitiesInfo.Dash ? { counterTackle: abilityToAction('Dash', data.abilitiesInfo.Dash, ctx) } : {}),
    // Counter Magic casts back the spell it was hit by (any flagged 0x40 in byte 3), so it carries all of them
    ...(unit.ReactionSkill === 'Counter Magic' && data.abilitiesInfo ? { counterMagic: Object.fromEntries(Object.keys(ABILITY_FLAGS).filter((n) => ABILITY_FLAGS[n][3] & 0x40 && data.abilitiesInfo[n]).map((n) => [n, abilityToAction(n, data.abilitiesInfo[n], ctx)]).filter(([, a]) => a && a.kind)) } : {}),
    // Counter Flood retaliates with the Elemental of the panel it stands on, so it carries all of them
    ...(unit.ReactionSkill === 'Counter Flood' && data.abilitiesInfo ? { counterFlood: Object.fromEntries([...new Set(ELEMENTAL_BY_SURFACE)].filter((n) => data.abilitiesInfo[n]).map((n) => [n, abilityToAction(n, data.abilitiesInfo[n], ctx)]).filter(([, a]) => a && a.kind === 'damage')) } : {}),
    ...(undead ? { undead: true } : {}), // skeleton/ghost families — healing hurts, ~50% revive
    ...(traits.perm.size ? { permStatus: [...traits.perm] } : {}),   // always on (Levitate's Float, Reflect Mail…)
    ...(traits.init.size ? { initStatus: [...traits.init] } : {}),   // on at the start (Angel Ring's Reraise…)
    ...(traits.immune.size ? { immune: [...traits.immune] } : {}),   // can't be given (Ribbon, a Wyvern's Poison…)
    evade, evadeSrc, ...(unit.ReactionSkill === 'Abandon' ? { abandon: true } : {}), ...(unit.SupportSkill === 'Beastmaster' || innates.some((i) => i && i.name === 'Beastmaster') ? { beastmaster: true } : {}), ...(hasSupport('Monster Talk') ? { monsterTalk: true } : {}), ...(hasSupport('Maintenance') ? { maintenance: true } : {}), actions,
    rel: reliance(actions, unit.Gender === 'Monster', unit, data),
    // what's in each slot a Steal or Rend can take (a monster wears nothing)
    worn: { weapon: !!eq.Mainhand, shield: equipped.Offhand.type === 'Shield' || equipped.Mainhand.type === 'Shield', head: !!eq.Head, armor: !!eq.Armor, accessory: !!eq.Accessory },
  };
  // Charge+N strikes as the basic Attack does, through the same exact weapon model (battleSim adds N), with both hands
  // under Dual Wield: FFTBG's Archer with a Blast Gun and a Glacier Gun charged into a Bolt and an Ice 2 (109 + 120), and
  // its AI scored Charge+1 96 where its Attack scored 89 (one strike would be about half). In the logged battles a
  // two-weapon unit's Charge landed two hits each time its hits were credited, a one-weapon unit's never more than one.
  // (and a Jump with the main hand through damage.js's jump model, whose PA is the term the modifiers scale)
  for (let k = 0; k < actions.length; k++) {
    if (actions[k].chargeN != null) actions[k] = { ...actions[k], weapon: { ...spec.weapon, ...(spec.power2 > 0 ? {} : { off: null }) }, ...(spec.power2 > 0 ? { power2: spec.power2, element2: spec.element2 } : {}) };
    else if (actions[k].jump) actions[k] = { ...actions[k], jumpWeapon: { main: spec.weapon.main, ctx: spec.weapon.ctx } };
  }
  // A transform carries the monster it turns its target into, prepped like any unit of that class (Brave and Faith are
  // the target's own, rebaked when it lands). FFTBG's transformed units came out at the Malboro's base numbers or close
  // (HP 427-457 of its 443, PA 12-14, MA 15-17, Speed 7) with Tendrils, Lick and Noxious Gas.
  for (const a of spec.actions || []) if (a.kind === 'transform' && typeof a.into === 'string') a.into = transformInto(a.into, data);
  return spec;
}

// A real unit → combatant spec. data = { charStats, itemschanged, notAbles, abilitiesInfo }.
// Carries the live runtime change-state (paBonus/maBonus/removed) + a `_rebake` closure so
// the engine can refresh the gear/PA·MA-derived fields when a stat-mod or steal/rend lands.
export function prepCombatant(unit, data) {
  const spec = computeBaked(unit, data, {});
  spec.paBonus = 0;
  spec.maBonus = 0;
  spec.removed = new Set();
  spec._rebake = (state) => computeBaked(unit, data, state);
  return spec;
}
const intoCache = new WeakMap();
function transformInto(cls, data) {
  const m = intoCache.get(data) || intoCache.set(data, new Map()).get(data);
  if (!m.has(cls)) m.set(cls, prepCombatant({ Name: cls, Class: cls, Gender: 'Monster', Brave: 50, Faith: 50, Mainhand: '', Offhand: '', Head: '', Armor: '', Accessory: '', ActionSkill: '', ReactionSkill: '', SupportSkill: '', MoveSkill: '', ClassSkills: [], ExtraSkills: [] }, data));
  return m.get(cls);
}

// The ability-formula context for a unit (PA/MA/etc.) — used to recompute a
// Mime's mirrored abilities with the MIME's own stats.
function unitCtx(unit, data) {
  const { equipped, stats, paUnarmed } = unitCombatStats(unit, data.charStats, data.itemschanged, data.notAbles);
  const wpRange = weaponRange(equipped.Mainhand);
  return { pa: stats.pa, ma: stats.ma, speed: stats.speed, brave: unit.Brave, faith: unit.Faith, wp: equipped.Mainhand.wp, maxHp: stats.hp, targetFaith: 50, weaponRange: wpRange, move: stats.move, weaponType: equipped.Mainhand.type, paUnarmed };
}

export function prepTeam(roster, data) {
  const specs = roster.map((u) => prepCombatant(u, data));
  // A Mime has NO actions of its own — its turn is just move + basic attack. What
  // it carries is a `mimicTable`: every teammate ability recomputed with the
  // MIME's own stats, keyed by name. The engine fires the matching entry whenever a
  // non-mime teammate acts (the mime copies it from its own tile at the same
  // relative offset — it can whiff or friendly-fire, like the real Mimic reaction).
  const donors = [];
  const seen = new Set();
  for (const u of roster) {
    if (u.Class === 'Mime' || u.Gender === 'Monster') continue;
    for (const n of [...(u.ClassSkills || []), ...(u.ExtraSkills || [])]) {
      if (!seen.has(n) && data.abilitiesInfo[n]) { seen.add(n); donors.push(n); }
    }
  }
  roster.forEach((u, i) => {
    if (u.Class !== 'Mime') return;
    const ctx = unitCtx(u, data);
    const table = {};
    for (const n of donors) { const a = abilityToAction(n, data.abilitiesInfo[n], ctx); if (a) table[n] = a; }
    // (and any other ability, copied the same way on first use: what a Charmed Mime echoes of its charmer's side)
    const mimicOf = (n) => { if (!(n in table)) { const info = data.abilitiesInfo[n]; table[n] = info ? abilityToAction(n, info, ctx) : null; } return table[n]; };
    specs[i] = { ...specs[i], actions: [], mimic: true, mimicTable: table, _mimicOf: mimicOf };
  });
  return specs;
}

// Diagnostic (NOT used in the hot path): how much of the monster→skills map the
// formula parser covers, with the unparsed skills listed. The prep silently skips
// misses (they'd need new abilityFormula families — stat buffs, MaxHP-CurrentHP,
// multi-status adds); this surfaces them for the test/tools to print. `data` only
// needs { monsterSkills, abilitiesInfo }. Drops the Beastmaster-locked '*' skills.
export function monsterSkillCoverage(data) {
  const ms = data.monsterSkills || {};
  const ctx = { pa: 14, ma: 20, speed: 8, brave: 70, faith: 60, wp: 0, maxHp: 400, targetFaith: 50, weaponRange: 1, move: 4, paUnarmed: 14 };
  let total = 0, parsed = 0; const missed = [];
  for (const cls of Object.keys(ms)) {
    for (const raw of ms[cls] || []) {
      if (typeof raw !== 'string' || raw.includes('*')) continue;
      total++;
      if (abilityToAction(raw, data.abilitiesInfo[raw], ctx)) parsed++;
      else missed.push(raw);
    }
  }
  return { total, parsed, missed: [...new Set(missed)].sort() };
}
